import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormattedValues } from '@marine/utils';
import { Button, Col, Divider, Dropdown, Form, InputNumber, Radio, Row } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useCallback, useMemo } from 'react';
import { AddReminderText } from './AddReminderText';
import styles from './ReminderStyles.module.scss';

interface Props {
  time?: string;
  timeUnit: number;
  timeUnitType: string | undefined;
  addReminder?: React.Dispatch<React.SetStateAction<boolean>>;
  timeUnitTypeFormName: string;
  timUnitFormName: string;
  largeDropdown?: boolean;
  showCustomInfo?: boolean;
}
const getTimeUnitInDays = (unit: number, unitType: string | undefined) => {
  if (unitType === 'd') {
    return unit;
  }

  if (unitType === 'm') {
    return unit * 30;
  }

  if (unitType === 'w') {
    return unit * 7;
  }

  if (unitType === 'y') {
    return unit * 365;
  }

  return unit;
};

const renderUnitType = (unitType: string | undefined, timeUnit: number | undefined) => {
  if (unitType === 'y') {
    return timeUnit === 1 ? 'Year' : 'Years';
  }
  if (unitType === 'm') {
    return timeUnit === 1 ? 'Month' : 'Months';
  }
  if (unitType === 'w') {
    return timeUnit === 1 ? 'Week' : 'Weeks';
  }

  return timeUnit === 1 ? 'Day' : 'Days';
};

const FormReminderDropdown: FC<Props> = ({
  time,
  timeUnit,
  timeUnitType,
  addReminder,
  timeUnitTypeFormName,
  timUnitFormName,
  largeDropdown,
  showCustomInfo = true,
}) => {
  const form = Form.useFormInstance();
  const { getFormattedDateTime } = useFormattedValues();
  const hasTimeUnit = Form.useWatch(timUnitFormName, form);

  const clearReminderInput = useCallback(() => {
    form.setFieldValue(timUnitFormName, null);

    if (addReminder) {
      addReminder(false);
    }
  }, [form, timUnitFormName, addReminder]);

  const renderDropdown = useMemo(() => {
    const inputDate = dayjs(time);
    const b = inputDate.subtract(getTimeUnitInDays(timeUnit, timeUnitType), 'days');

    return (
      <div className={largeDropdown ? styles.largeReminderDropdownContainer : styles.reminderDropdownContainer}>
        <Form.Item name={timeUnitTypeFormName} style={{ marginBottom: '8px' }}>
          <Radio.Group defaultValue="d" buttonStyle="solid" size="middle" value="d">
            <Radio.Button value="d">Day</Radio.Button>
            <Radio.Button value="w">Week</Radio.Button>
            <Radio.Button value="m">Month</Radio.Button>
            <Radio.Button value="y">Year</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Divider className={styles.dividerStyling} />

        <Row className={styles.secondRow}>
          <Col span={12}>
            <p className={styles.daysInAdvance}>{renderUnitType(timeUnitType, timeUnit) || 'X'} in advance</p>
          </Col>

          <Col span={12} className={styles.formInputColumn}>
            <Form.Item
              name={timUnitFormName}
              rules={[
                {
                  type: 'integer',
                  message: 'Please enter a whole number.',
                },
              ]}
            >
              <InputNumber
                className={styles.reminderInput}
                controls={false}
                max={1000}
                min={1}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        {timeUnit && showCustomInfo && (
          <p className={styles.descriptionParagraph}>
            {timeUnit ? timeUnit : 'X'} {renderUnitType(timeUnitType, timeUnit).toLocaleLowerCase()} earlier. Remind me
            at
            <br />
            {getFormattedDateTime(b) ? getFormattedDateTime(b) : '----'}
          </p>
        )}
        {!!hasTimeUnit && (
          <Button
            icon={<CloseCircleOutlined />}
            type="default"
            onClick={() => clearReminderInput()}
            className={styles.removeReminderButton}
          >
            Remove reminder
          </Button>
        )}
      </div>
    );
  }, [
    getFormattedDateTime,
    time,
    timeUnit,
    timeUnitType,
    timUnitFormName,
    timeUnitTypeFormName,
    largeDropdown,
    showCustomInfo,
    clearReminderInput,
    hasTimeUnit,
  ]);

  return (
    <Dropdown popupRender={() => renderDropdown} trigger={['hover']} destroyOnHidden>
      <Button onClick={() => addReminder && addReminder(true)} className={styles.reminderInfoButton}>
        <AddReminderText timeUnit={timeUnit} timeUnitType={timeUnitType} renderUnitType={renderUnitType} />
      </Button>
    </Dropdown>
  );
};

export { FormReminderDropdown };
